import { Link } from 'react-router-dom';
import { ArrowRight, RotateCcw } from 'lucide-react';
import { formatPeriod } from '../../utils/periodUtils';
import { formatPersonName } from '../../utils/formatName';
import { pendingPeriod } from './pendingGroups';
import type { QueueDocument } from './useDashboardData';
import { DashboardTrends } from './dashboardTrends';
import WeeklyChart from './WeeklyChart';
import { AgePill, AiPill, Card, DataState, daysLabel, linkClass, Meter, Pill, Stat } from './ui';
import { AsyncState } from './useAsyncData';
import { REVIEW_TARGET_DAYS, WorkloadData } from './workload';

const number = (value: number) => value.toLocaleString('es-MX', { maximumFractionDigits: 1 });
const shortDate = (value: string) => new Date(value + 'T12:00:00Z').toLocaleDateString('es-MX', { day: 'numeric', month: 'short', timeZone: 'UTC' });
const PRIORITY_LIMIT = 10;
const AGING_FILL: Record<string, string> = { ok: 'bg-emerald-600', watch: 'bg-amber-500', late: 'bg-orange-500', critical: 'bg-red-600' };

export const WORK_RETURN_TO = '/admin/dashboard?vista=trabajo';

export function reviewUrl(doc: QueueDocument) {
  const { month, year } = pendingPeriod(doc);
  return `/review/${encodeURIComponent(doc.user_id)}?${new URLSearchParams({ selectedMonth: formatPeriod(month, year), documentId: doc.document_id, returnTo: WORK_RETURN_TO })}`;
}

function QueueHealth({ data }: { data: WorkloadData }) {
  const late = data.oldestDays != null && data.oldestDays > 30;
  return (
    <section aria-label="Estado de la cola" className="grid gap-px overflow-hidden rounded-xl border border-slate-200 bg-slate-200 sm:grid-cols-2 lg:grid-cols-4">
      <Stat
        label="Por revisar"
        value={number(data.toReview)}
        tone={data.pastTarget > 0 ? 'watch' : 'good'}
        hint={data.pastTarget > 0 ? `${number(data.pastTarget)} llevan más de ${REVIEW_TARGET_DAYS} días (objetivo interno)` : `Todo dentro del objetivo de ${REVIEW_TARGET_DAYS} días`}
      />
      <Stat
        label="Más antiguo"
        value={data.oldestDays == null ? '—' : daysLabel(data.oldestDays)}
        tone={data.oldestDays == null ? 'neutral' : late ? 'bad' : data.oldestDays > REVIEW_TARGET_DAYS ? 'watch' : 'good'}
        hint="Desde la carga del archivo"
      />
      <Stat
        label="Esperando al proveedor"
        value={number(data.waitingOnSupplier)}
        hint="Revisión solicitada; sin acción tuya por ahora"
      />
      <Stat
        label="Decididos · 7 días"
        value={number(data.decidedThisWeek.approved + data.decidedThisWeek.rejected)}
        hint={`${number(data.decidedThisWeek.approved)} aprobados · ${number(data.decidedThisWeek.rejected)} rechazados · ${number(data.decidedThisWeek.mine)} tuyos`}
      />
    </section>
  );
}

function Aging({ data }: { data: WorkloadData }) {
  const total = data.aging.reduce((sum, b) => sum + b.count, 0);
  return (
    <Card
      labelledBy="aging-title"
      title="Antigüedad de la cola"
    >
      <div className="px-5 py-4">
        {total === 0 ? (
          <p className="text-sm text-slate-600">No hay documentos por revisar.</p>
        ) : (
          <>
            <div className="flex h-3 w-full overflow-hidden rounded-full bg-slate-100" aria-hidden="true">
              {data.aging.map((b) => b.count > 0 && <div key={b.id} className={AGING_FILL[b.tone]} style={{ width: `${(b.count / total) * 100}%` }} />)}
            </div>
            <ul className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
              {data.aging.map((b) => (
                <li key={b.id} className="flex items-start gap-2">
                  <span className={`mt-1 h-2.5 w-2.5 shrink-0 rounded-sm ${AGING_FILL[b.tone]}`} aria-hidden="true" />
                  <span>
                    <span className="block text-lg font-semibold tabular-nums text-slate-900">{number(b.count)}</span>
                    <span className="block text-xs text-slate-500">{b.label}</span>
                  </span>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    </Card>
  );
}

function Priority({ data }: { data: WorkloadData }) {
  const rows = data.priority.slice(0, PRIORITY_LIMIT);
  return (
    <Card
      labelledBy="priority-title"
      title="Atender primero"
      subtitle={`Primero lo que supera ${REVIEW_TARGET_DAYS} días, luego los reenvíos`}
      action={<Link to="/review" className={`${linkClass} text-sm`}>Ver cola completa</Link>}
    >
      {rows.length === 0 ? (
        <p className="px-5 py-8 text-sm text-slate-600">No hay documentos por revisar. Buen trabajo.</p>
      ) : (
        <ul className="divide-y divide-slate-100">
          {rows.map((group) => {
            const doc = group.oldest;
            const name = formatPersonName(doc.user_full_name) || doc.user_email;
            return (
              <li key={group.key}>
                <Link
                  to={reviewUrl(doc)}
                  className="group grid gap-2 px-5 py-3 hover:bg-slate-50 focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-blue-600 sm:grid-cols-[auto_minmax(0,1fr)_auto] sm:items-center sm:gap-4"
                  aria-label={`Revisar ${group.documentName} de ${name}, ${daysLabel(group.ageDays)} en la cola${group.resubmission ? ', reenvío tras rechazo' : ''}`}
                >
                  <AgePill days={group.ageDays} />
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-slate-900">
                      {group.documentName}
                      <span className="font-normal text-slate-500"> · {group.count > 1 ? `${group.count} periodos (${group.rangeLabel})` : group.rangeLabel}</span>
                    </p>
                    <p className="mt-0.5 truncate text-xs text-slate-500">{name} · {doc.organization_name || 'Sin organización'}</p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    {group.resubmission && <Pill tone="neutral"><RotateCcw className="h-3 w-3" aria-hidden="true" />Reenvío</Pill>}
                    <AiPill recommendation={group.ai} />
                    <ArrowRight className="hidden h-4 w-4 text-slate-400 group-hover:text-blue-700 sm:block" aria-hidden="true" />
                  </div>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
      {data.priority.length > PRIORITY_LIMIT && (
        <p className="border-t border-slate-100 px-5 py-3 text-xs text-slate-500">
          {PRIORITY_LIMIT} de {number(data.priority.length)} grupos · <Link to="/review" className={linkClass}>ver todos en la cola</Link>
        </p>
      )}
    </Card>
  );
}

function AiAgreementCard({ data }: { data: WorkloadData }) {
  const { ai } = data;
  const decided = ai.total - ai.uncertain;
  const rate = decided > 0 ? Math.round((ai.agreed / decided) * 100) : null;
  const rows = [
    { label: 'Coinciden con la IA', value: ai.agreed, tone: 'good' as const },
    { label: 'Aprobados aunque la IA sugería rechazar', value: ai.approvedAgainstAi, tone: 'bad' as const },
    { label: 'Rechazados aunque la IA sugería aprobar', value: ai.rejectedAgainstAi, tone: 'watch' as const },
    { label: 'La IA no llegó a una conclusión', value: ai.uncertain, tone: 'neutral' as const },
  ];
  return (
    <Card labelledBy="ai-title" title="IA frente a la decisión humana" subtitle="Últimos 90 días">
      <div className="px-5 py-4">
        {ai.total === 0 ? (
          <p className="text-sm text-slate-600">Aún no hay decisiones con análisis de IA previo.</p>
        ) : (
          <>
            <p className="text-3xl font-semibold tabular-nums text-slate-900">{rate == null ? '—' : `${rate}%`}</p>
            <p className="text-xs text-slate-500">de coincidencia en {number(decided)} decisiones con recomendación</p>
            <ul className="mt-4 space-y-3">
              {rows.map((row) => (
                <li key={row.label}>
                  <div className="mb-1 flex justify-between gap-3 text-xs"><span className="text-slate-600">{row.label}</span><span className="font-semibold tabular-nums text-slate-900">{number(row.value)}</span></div>
                  <Meter value={row.value} max={ai.total} tone={row.tone} />
                </li>
              ))}
            </ul>
            {ai.approvedAgainstAi > 0 && (
              <p className="mt-4 rounded-lg bg-amber-50 p-3 text-xs leading-relaxed text-amber-900">
                Revisa las aprobaciones contra la IA: indican criterios demasiado estrictos o evidencia incompleta que no respaldaría la deducción.
              </p>
            )}
          </>
        )}
      </div>
    </Card>
  );
}

function Team({ data }: { data: WorkloadData }) {
  const peak = Math.max(1, ...data.team.map((r) => r.approved + r.rejected));
  return (
    <Card labelledBy="team-title" title="Equipo · 7 días">
      {data.team.length === 0 ? (
        <p className="px-5 py-6 text-sm text-slate-600">Sin decisiones esta semana.</p>
      ) : (
        <ul className="space-y-3 px-5 py-4">
          {data.team.map((r) => (
            <li key={r.id}>
              <div className="mb-1 flex justify-between gap-3 text-xs">
                <span className="truncate text-slate-700">{formatPersonName(r.name)}</span>
                <span className="shrink-0 tabular-nums text-slate-500"><span className="font-semibold text-slate-900">{number(r.approved + r.rejected)}</span> · {number(r.rejected)} rech.</span>
              </div>
              <div className="flex h-1.5 w-full overflow-hidden rounded-full bg-slate-100" aria-hidden="true">
                <div className="bg-emerald-600" style={{ width: `${(r.approved / peak) * 100}%` }} />
                <div className="bg-red-600" style={{ width: `${(r.rejected / peak) * 100}%` }} />
              </div>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

export function WeeklyFlow({ trends }: { trends: AsyncState<DashboardTrends> }) {
  const weeks = trends.data?.weeks || [];
  const peak = Math.max(1, ...weeks.flatMap((w) => [w.received, w.approved + w.rejected]));
  const series = [
    { key: 'received', label: 'Recibidos', fill: 'bg-blue-600' },
    { key: 'approved', label: 'Aprobados', fill: 'bg-emerald-600' },
    { key: 'rejected', label: 'Rechazados', fill: 'bg-red-600' },
  ] as const;
  return (
    <Card labelledBy="flow-title" title="Flujo semanal" subtitle={`8 semanas · máximo ${number(peak)} por semana`}>
      <DataState loading={trends.loading} error={trends.error} retry={trends.reload} rows={2} />
      {!trends.loading && trends.data && (
        <div className="px-5 pb-4">
          <WeeklyChart
            heading="Documentos recibidos, aprobados y rechazados por semana"
            labels={weeks.map((w) => `Semana del ${shortDate(w.start)}: ${number(w.received)} recibidos · ${number(w.approved)} aprobados · ${number(w.rejected)} rechazados`)}
          >
            {(active) => (
              <div className="flex h-full items-end">
                {weeks.map((w, i) => (
                  <div key={w.start} className={`flex h-full min-w-0 flex-1 items-end justify-center gap-1 rounded-sm ${active === i ? 'bg-slate-100' : ''}`}>
                    <span className={`w-2.5 rounded-t-sm bg-blue-600 ${active != null && active !== i ? 'opacity-40' : ''}`} style={{ height: `${(w.received / peak) * 100}%` }} />
                    <span className={`flex w-2.5 flex-col justify-end ${active != null && active !== i ? 'opacity-40' : ''}`} style={{ height: `${((w.approved + w.rejected) / peak) * 100}%` }}>
                      <span className="w-full rounded-t-sm bg-red-600" style={{ height: `${w.approved + w.rejected ? (w.rejected / (w.approved + w.rejected)) * 100 : 0}%` }} />
                      <span className="w-full flex-1 bg-emerald-600" />
                    </span>
                  </div>
                ))}
              </div>
            )}
          </WeeklyChart>
          <div className="mt-1 flex justify-between text-xs text-slate-500"><span>{weeks[0] && shortDate(weeks[0].start)}</span><span>{weeks.length > 0 && shortDate(weeks[weeks.length - 1].start)}</span></div>
          <div className="mt-3 flex flex-wrap gap-4 text-xs text-slate-600">
            {series.map((s) => <span key={s.key}><span className={`mr-1 inline-block h-2 w-2 rounded-sm ${s.fill}`} aria-hidden="true" />{s.label}</span>)}
          </div>
        </div>
      )}
    </Card>
  );
}

export default function WorkView({ workload, trends }: { workload: AsyncState<WorkloadData>; trends: AsyncState<DashboardTrends> }) {
  if (workload.loading || workload.error || !workload.data)
    return (
      <div className="rounded-xl border border-slate-200 bg-white">
        <DataState loading={workload.loading} error={workload.error} retry={workload.reload} rows={5} />
      </div>
    );
  const data = workload.data;
  return (
    <div className="space-y-6">
      <QueueHealth data={data} />
      <div className="grid gap-6 xl:grid-cols-[minmax(0,1.6fr)_minmax(320px,1fr)]">
        <div className="min-w-0 space-y-6">
          <Priority data={data} />
          <WeeklyFlow trends={trends} />
        </div>
        <div className="min-w-0 space-y-6">
          <Aging data={data} />
          <AiAgreementCard data={data} />
          <Team data={data} />
        </div>
      </div>
    </div>
  );
}
